import { ContentCopyRounded, SaveAltRounded } from "@mui/icons-material";
import { Box, IconButton, Tooltip } from "@mui/material";
import React from "react";
import copy from "copy-to-clipboard";
import ReactDiffViewer from "react-diff-viewer-continued";

type JSONViewerProps = {
    value: unknown;
    display?: "raw" | "rich";
    /**
     * When provided (rich display only), shows a Save button next to Copy that
     * downloads the JSON as a file with this name. `.json` is appended if missing.
     */
    downloadFileName?: string;
} & React.ComponentPropsWithoutRef<typeof Box>;

export function JSONViewer({
    value,
    display = "raw",
    downloadFileName,
    ...props
}: JSONViewerProps) {
    // If value is already a JSON string, parse it first
    let parsedValue = value;
    if (typeof value === "string") {
        try {
            parsedValue = JSON.parse(value);
        } catch {
            // If parsing fails, use the string as-is
            parsedValue = value;
        }
    }

    // Valid, canonical JSON — used for copy and download.
    const jsonString = JSON.stringify(parsedValue, null, 2) ?? String(value);
    // Unescape newlines/quotes only for on-screen readability. This is NOT valid
    // JSON (embedded quotes/newlines in string values become unescaped), so it must
    // never be used for copy or download — see jsonString above.
    const displayString = jsonString.replace(/\\n/g, "\n").replace(/\\"/g, '"');

    const handleDownload = () => {
        if (!downloadFileName) return;
        const fileName = downloadFileName.endsWith(".json")
            ? downloadFileName
            : `${downloadFileName}.json`;
        const blob = new Blob([jsonString], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = fileName;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        URL.revokeObjectURL(url);
    };

    if (display === "rich") {
        return (
            <Box
                {...props}
                sx={[
                    {
                        maxWidth: "100%",
                    },
                    ...(Array.isArray(props.sx) ? props.sx : [props.sx]),
                ]}
            >
                <Box
                    sx={{
                        alignItems: "center",
                        backgroundColor: "#fafbfc",
                        display: "flex",
                        height: 32,
                        justifyContent: "flex-end",
                        px: 1,
                    }}
                >
                    <Tooltip title="Copy JSON">
                        <IconButton
                            aria-label="Copy JSON"
                            onClick={() => copy(jsonString)}
                            size="small"
                        >
                            <ContentCopyRounded fontSize="inherit" />
                        </IconButton>
                    </Tooltip>
                    {downloadFileName && (
                        <Tooltip title="Save JSON to file">
                            <IconButton
                                aria-label="Save JSON to file"
                                onClick={handleDownload}
                                size="small"
                            >
                                <SaveAltRounded fontSize="inherit" />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
                <Box
                    sx={{
                        maxWidth: "100%",
                        overflowX: "auto",
                        "& table": {
                            tableLayout: "auto",
                            width: "max-content",
                            minWidth: "100%",
                        },
                        "& table colgroup col:nth-of-type(2)": {
                            display: "none",
                        },
                        "& table tbody tr td:first-of-type": {
                            backgroundColor: "#f7f7f7",
                            left: 0,
                            position: "sticky",
                            zIndex: 1,
                        },
                        "& table tbody tr td:nth-of-type(2)": {
                            display: "none",
                        },
                        "& table td": {
                            overflow: "visible",
                        },
                    }}
                >
                    <ReactDiffViewer
                        oldValue={displayString}
                        newValue={displayString}
                        useDarkTheme={false}
                        splitView={false}
                        showDiffOnly={false}
                        hideSummary
                        styles={{
                            diffContainer: {
                                minWidth: "unset",
                                overflowX: "visible",
                                tableLayout: "auto",
                            },
                            line: { fontFamily: "monospace", fontSize: 12 },
                            content: {
                                fontSize: 12,
                                overflow: "visible",
                                width: "auto",
                            },
                            contentText: {
                                whiteSpace: "pre",
                                lineBreak: "auto",
                            },
                        }}
                    />
                </Box>
            </Box>
        );
    }

    return (
        <Box
            component="pre"
            {...props}
            sx={[
                {
                    whiteSpace: "pre-wrap",
                    fontFamily: "monospace",
                    fontSize: "0.8em",
                },
                ...(Array.isArray(props.sx) ? props.sx : [props.sx]),
            ]}
        >
            <code>{displayString}</code>
        </Box>
    );
}
